import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QuestionLayout } from 'components/layout/QuestionLayout';
import { QuestionFlowStage } from 'components/QuestionFlow/QuestionFlowStage';
import { ModuleCompletionCard } from 'components/QuestionFlow/ModuleCompletionCard';
import { FlowThemeLayer, FLOW_THEMES, isFlowThemeId, resolveFlowTheme } from 'components/QuestionFlow/themes';
import { LIFE_PLAN_PARTS } from 'components/QuestionFlow/themes/lifePlan/catalog';
import { clearLifePlanLiveAnswer, setLifePlanLiveAnswer, themeAnswerFromResponse, themeAnswersFromResponses } from 'components/QuestionFlow/themes/lifePlan/answers';
import { useDynamicThemeContext, ThemeContextProvider } from 'components/QuestionFlow/themes/DynamicThemeContext';
import type { ThemeContextInput } from 'components/QuestionFlow/themes/themeContext';
import type { SubmitQuestionOptions } from 'components/QuestionFlow/useQuestionFlowManager';
import type { Flow, Question, ResponsePayload, ThemeAnswer } from 'types';
import { AnswerReferenceContext, type AnswerReferenceContextValue } from 'utils/answerReferences';
import { normalizePreviewSettings } from './previewSettings';
import labContent from './lifePlanLabContent.json';
import labAnswers from './lifePlanLabAnswers.json';

/**
 * Life Plan lab: plays the real, fixed Life Plan (20 modules, 131 questions) through the real question player and
 * theme layer with fictional data only (a learner called Maya). Route:
 * `#/life-plan-lab/<themeId>?module=8&step=3&name=Maya&answers=all`. `answers=all` pre-fills the current module
 * too (for page reviews); by default only earlier modules are answered and the open module is answered live.
 * Recorders drive it with `window.__lifePlanLab`. Nothing is fetched, saved or sent.
 */
interface LabOption { front: string; back?: string; other?: boolean }
interface LabQuestion { key: string; type: string; header: string; text: string; subtext: string; low: string; high: string; options: LabOption[]; metadata: Record<string, unknown> }
interface LabModule { number: number; slug: string; title: string; part: string; questions: LabQuestion[] }
const MODULES = labContent as unknown as LabModule[];
const PART_BY_ID = new Map(LIFE_PLAN_PARTS.map(part => [part.id, part]));
type PersonaAnswer = { text?: string; items?: string[]; rating?: number; flag?: boolean };
const PERSONA = labAnswers as unknown as Record<string, PersonaAnswer>;

const themeAnswer = (answer: PersonaAnswer): ThemeAnswer => ({
  text: answer.text ?? (answer.items ?? []).join(', ') ?? String(answer.rating ?? ''),
  items: answer.items ?? [],
  rating: typeof answer.rating === 'number' ? answer.rating : null,
  flag: typeof answer.flag === 'boolean' ? answer.flag : null,
});

export interface LifePlanLabApi {
  state(): { module: number; moduleTitle: string; partTitle: string; step: number; total: number; key: string | null; type: string | null; complete: boolean; ready: boolean; theme: string };
  goto(module: number, step?: number): void;
  next(): void;
  prev(): void;
  setName(name: string): void;
  /** Maya's answer to a question (for recorders that type it into the card). */
  persona(key: string): PersonaAnswer | null;
}
declare global { interface Window { __lifePlanLab?: LifePlanLabApi } }

const toQuestion = (module: LabModule, question: LabQuestion, index: number): Question => {
  const id = module.number * 1000 + index + 1;
  // Callback summaries are written by AI from earlier answers and edited by the learner: the lab shows Maya's
  // edited summary in an editable box, as the real card does.
  const type = question.type === 'callback_summary' ? 'text_or_audio' : question.type;
  return {
    id, key: question.key, category: 'Life Plan', question_type: type, question_type_display: question.type === 'callback_summary' ? 'callback_summary' : type, is_active: true, order: index + 1,
    header: question.header || null, subtext: question.subtext || null, text: question.text,
    ...(question.low ? { low_text: question.low } : {}), ...(question.high ? { high_text: question.high } : {}),
    options: question.options.map((option, optionIndex) => ({
      id: id * 100 + optionIndex + 1, front_text: option.front, back_text: option.back ?? '', value: optionIndex + 1,
      order: optionIndex + 1, is_other: Boolean(option.other),
    })),
    metadata: question.metadata,
  } as Question;
};

/** Maya's answer as the response the card would hold. */
const personaResponse = (question: Question, completed: boolean): ResponsePayload | null => {
  const answer = PERSONA[question.key ?? ''];
  if (!answer || typeof question.id !== 'number') return null;
  const ids = (labels: string[] | undefined) => (labels ?? []).map(label => question.options?.find(option => option.front_text === label)?.id).filter((id): id is number => typeof id === 'number');
  const response: ResponsePayload = { question: question.id, is_completed: completed };
  switch (question.question_type) {
    case 'text_or_audio': response.text_value = answer.text ?? ''; break;
    case 'text_list': response.text_list = answer.items ?? []; break;
    case 'rating': response.rating_value = answer.rating; break;
    case 'boolean': response.boolean_value = answer.flag; break;
    case 'multiple_reveal': response.revealed_options = ids(answer.items); break;
    default: response.selected_options = ids(answer.items); break;
  }
  return response;
};

const clampInt = (value: number, min: number, max: number) => Math.min(max, Math.max(min, Math.floor(Number.isFinite(value) ? value : min)));

function LifePlanPlayer({ themeId }: { themeId: string }) {
  const theme = resolveFlowTheme(themeId);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preview = useMemo(() => normalizePreviewSettings(window.__WF_THEME_PREVIEW__), []);
  const prefillAll = search.get('answers') === 'all';
  const [firstName, setFirstName] = useState(search.get('name') ?? 'Maya');
  const [location, setLocation] = useState(() => {
    const moduleNumber = clampInt(Number(search.get('module') ?? 1), 1, MODULES.length);
    return { module: moduleNumber, step: clampInt(Number(search.get('step') ?? 0), 0, MODULES[moduleNumber - 1].questions.length), furthest: 0 };
  });
  const [direction, setDirection] = useState<1 | -1>(1);
  const [ready, setReady] = useState(false);
  // Like a route change in the real player, the outgoing module's scene leaves before the next one mounts (under
  // the retained still), so the next scene takes over as a handoff instead of fading in.
  const [mountedModule, setMountedModule] = useState(location.module);
  useEffect(() => {
    if (mountedModule === location.module) return undefined;
    const frame = window.requestAnimationFrame(() => setMountedModule(location.module));
    return () => window.cancelAnimationFrame(frame);
  }, [mountedModule, location.module]);
  const transitionId = useRef(0);
  // The real player holds a still of the outgoing scene while the next module's scene starts; the lab mimics it.
  const [retained, setRetained] = useState<HTMLCanvasElement | null>(null);
  const [retainedFading, setRetainedFading] = useState(false);
  const retainedHost = useRef<HTMLDivElement>(null);

  const labModule = MODULES[location.module - 1];
  const questions = useMemo(() => labModule.questions.map((question, index) => toQuestion(labModule, question, index)), [labModule]);
  // Responses for the open module: Maya's (all of them with `answers=all`), otherwise only the cards that come
  // pre-filled in the real player (her edited summaries; sorting starts placed so a recorder need not drag).
  const initialResponses = useCallback((list: Question[]) => Object.fromEntries(list.flatMap((question): [number, ResponsePayload][] => {
    const prefilled = labModule.questions.find(item => item.key === question.key)?.type;
    const response = personaResponse(question, prefillAll);
    if (!response) return [];
    if (prefillAll || prefilled === 'callback_summary' || prefilled === 'horizontal_drag_drop') return [[question.id as number, response]];
    return [];
  })), [labModule, prefillAll]);
  // Responses belong to their module: on a jump the next module's cards get theirs on their very first render
  // (a card that mounted empty would otherwise save the empty draft over its answer).
  const [store, setStore] = useState<{ module: number; values: Record<number, ResponsePayload> }>(() => ({ module: location.module, values: initialResponses(questions) }));
  const responses = useMemo(() => (store.module === location.module ? store.values : initialResponses(questions)), [store, location.module, initialResponses, questions]);
  useEffect(() => {
    if (store.module === location.module) return;
    setStore({ module: location.module, values: initialResponses(questions) });
    clearLifePlanLiveAnswer();
  }, [store.module, location.module, initialResponses, questions]);
  useEffect(() => () => clearLifePlanLiveAnswer(), []);

  const step = Math.min(location.step, questions.length);
  const complete = step === questions.length;
  const part = PART_BY_ID.get(labModule.part as never) ?? LIFE_PLAN_PARTS[0];
  const progress = { step, total: questions.length, complete };
  const flow: Flow = { id: location.module, name: labModule.slug, key: labModule.slug, title: labModule.title, description: 'Fictional Life Plan preview', questions, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z', theme: theme.id } as Flow;
  const completedModules = location.module - 1 + Number(complete);

  // Earlier modules: what was submitted in this session, else Maya's saved answers (what the server sends); this
  // module: the answers given so far.
  const submitted = useRef<Record<string, ThemeAnswer>>({});
  const earlier = useMemo(() => Object.fromEntries(MODULES.slice(0, location.module - 1).flatMap(module => module.questions)
    .flatMap(question => (submitted.current[question.key] ? [[question.key, submitted.current[question.key]]] : PERSONA[question.key] ? [[question.key, themeAnswer(PERSONA[question.key])]] : []))), [location.module]);
  const lifePlan = useMemo(() => ({ answers: { ...earlier, ...themeAnswersFromResponses(questions, responses) } }), [earlier, questions, responses]);
  const references = useMemo<AnswerReferenceContextValue>(() => ({
    mode: 'runtime',
    references: Object.fromEntries(Object.entries(lifePlan.answers).map(([key, value]) => [key, { status: 'answered' as const, text: value.items.length ? value.items.join(', ') : value.text, items: value.items }])),
  }), [lifePlan]);

  const themeInput = useMemo<ThemeContextInput>(() => ({
    identity: { firstName, locale: preview.locale, direction: preview.direction },
    content: {
      images: [], course: { key: 'life-plan', title: 'Life Plan' },
      chapter: part.chapterKey ? { key: part.chapterKey, title: part.chapterTitle ?? part.title, number: part.order, total: 3 } : null,
      module: { key: labModule.slug, title: labModule.title, number: location.module, total: MODULES.length, estimatedMinutes: Math.max(2, Math.round(questions.length * 0.8)) },
      activity: complete ? null : { key: questions[step].key ?? String(step), title: questions[step].header ?? '', type: questions[step].question_type },
    },
    progress: { position: step, total: questions.length, completed: location.furthest, furthest: Math.max(step, location.furthest), complete, visit: 'starting', completedModules, courseFraction: completedModules / MODULES.length },
    journey: MODULES.map(module => ({ key: module.slug, title: module.title, number: module.number, state: module.number === location.module ? (complete ? 'completed' as const : 'current' as const) : module.number < location.module ? 'completed' as const : 'available' as const })),
    state: { screen: complete ? 'completion' : step === 0 ? 'introduction' : 'activity', direction: direction === 1 ? 'forward' : 'backward', answerSelected: !complete && Boolean(responses[questions[step].id as number]), answerSubmitted: step < location.furthest },
    events: [{ id: `lab-${transitionId.current}-${location.module}-${step}`, type: complete ? 'module-completed' : 'step-entered', key: `${location.module}-${step}` }],
    preferences: preview, preview: { enabled: true, recording: preview.recording || search.get('recording') === '1', time: preview.fixedTime }, visualSeed: `${preview.seed}:${labModule.slug}`,
    lifePlan,
  }), [firstName, preview, part, labModule, location, questions, step, complete, completedModules, direction, responses, search, lifePlan]);
  const themeContext = useDynamicThemeContext(themeInput);

  const go = useCallback((moduleNumber: number, nextStep: number, dir: 1 | -1) => {
    transitionId.current += 1; setDirection(dir);
    const target = clampInt(moduleNumber, 1, MODULES.length);
    setLocation(previous => ({ module: target, step: clampInt(nextStep, 0, MODULES[target - 1].questions.length), furthest: target === previous.module ? Math.max(previous.furthest, nextStep) : nextStep }));
    if (target !== location.module) {
      const source = document.querySelector<HTMLCanvasElement>('[data-testid="flow-theme-backdrop"] canvas');
      if (source && source.width && source.height) {
        const copy = document.createElement('canvas');
        copy.width = source.width; copy.height = source.height;
        copy.getContext('2d')?.drawImage(source, 0, 0);
        Object.assign(copy.style, { width: '100%', height: '100%', display: 'block' });
        setRetained(copy); setRetainedFading(false);
      }
      setReady(false);
    }
  }, [location.module]);
  useEffect(() => {
    const host = retainedHost.current;
    if (!retained || !host) return undefined;
    host.replaceChildren(retained);
    return undefined;
  }, [retained]);
  useEffect(() => {
    if (!retained || !ready) return undefined;
    // Like the real player: the still fades out as soon as the next module's scene has drawn (450 ms).
    const fade = window.setTimeout(() => setRetainedFading(true), 50);
    const remove = window.setTimeout(() => setRetained(null), 600);
    return () => { window.clearTimeout(fade); window.clearTimeout(remove); };
  }, [retained, ready]);
  const advance = useCallback(() => go(location.module, Math.min(step + 1, questions.length), 1), [go, location.module, step, questions.length]);
  const back = useCallback(() => go(location.module, Math.max(0, step - 1), -1), [go, location.module, step]);
  const submit = useCallback((response: ResponsePayload, options?: SubmitQuestionOptions) => {
    const answered = questions.find(candidate => candidate.id === response.question);
    const shown = answered?.key ? themeAnswerFromResponse(answered, response) : null;
    if (answered?.key && shown) submitted.current[answered.key] = shown;
    setStore(previous => ({
      module: location.module,
      values: { ...(previous.module === location.module ? previous.values : initialResponses(questions)), [response.question]: { ...response, is_completed: true } },
    }));
    if (options?.advance !== false) advance();
  }, [advance, location.module, initialResponses, questions]);
  const onDraft = useCallback((questionId: number, draft: ResponsePayload | null) => {
    const question = questions.find(candidate => candidate.id === questionId);
    if (question?.key) setLifePlanLiveAnswer(question.key, themeAnswerFromResponse(question, draft));
  }, [questions]);

  const snapshot = { go, advance, back, step, total: questions.length, complete, ready, key: complete ? null : questions[step].key ?? null, type: complete ? null : labModule.questions[step].type, module: location.module, moduleTitle: labModule.title, partTitle: part.title, theme: theme.id, answerKeys: Object.keys(lifePlan.answers) };
  const latest = useRef(snapshot);
  latest.current = snapshot;
  useEffect(() => {
    window.__lifePlanLab = {
      state: () => { const { go: _go, advance: _advance, back: _back, ...state } = latest.current; return state; },
      goto: (moduleNumber, nextStep = 0) => latest.current.go(moduleNumber, nextStep, 1),
      next: () => latest.current.advance(),
      prev: () => latest.current.back(),
      setName: setFirstName,
      persona: (key) => PERSONA[key] ?? null,
    };
    return () => { delete window.__lifePlanLab; };
  }, []);

  return <ThemeContextProvider value={themeContext}>
    <AnswerReferenceContext.Provider value={references}>
      <div dir={preview.direction} lang={preview.locale} className="relative isolate min-h-screen" data-testid="theme-studio-demo" data-demo-step={step} data-demo-total={questions.length}
        data-lab-module={location.module} data-lab-step={step} data-lab-key={complete ? '' : questions[step].key} data-lab-ready={ready ? 'true' : 'false'}>
        {retained ? <div ref={retainedHost} aria-hidden="true" className="pointer-events-none fixed inset-0 -z-[5]" style={{ opacity: retainedFading ? 0 : 1, transition: 'opacity 450ms ease' }} /> : null}
        {mountedModule === location.module ? <FlowThemeLayer key={location.module} theme={theme.id} progress={progress} themeContext={themeContext} onBackToCourse={() => navigate('/life-plan-lab')} onReadyChange={setReady} /> : null}
        <QuestionLayout progress={step / questions.length * 100} flowName={labModule.title} theme={theme.id} themeProgress={progress} themeReady={ready}
          backAction={{ label: 'Back to course', onClick: () => navigate('/life-plan-lab') }}>
          <QuestionFlowStage isAtFlowEnd={complete} transitionDirection={direction} question={questions[step] ?? null}
            questionResponse={responses[(questions[step]?.id as number) ?? -1]} isSubmitting={false} isLastQuestion={step === questions.length - 1}
            canGoToPrevious={step > 0} onPrevious={back} onQuestionSubmit={submit} onQuestionDraftChange={onDraft} flow={flow} flowRunId={null} currentQuestionIndex={step} questionIndex={step}
            debugMetadata={null} completionContext={{ responsesByKey: {}, targetDate: null }} lastQuestionButtonText="Finish Module" showAccountTools={false}
            completionContent={<ModuleCompletionCard title={labModule.title} primaryActionLabel={location.module < MODULES.length ? 'Continue' : 'Finish Life Plan'} onContinue={() => location.module < MODULES.length ? go(location.module + 1, 0, 1) : navigate('/life-plan-lab')} returnLabel="Back to course" onReturnToDashboard={() => navigate('/life-plan-lab')} />} />
        </QuestionLayout>
      </div>
    </AnswerReferenceContext.Provider>
  </ThemeContextProvider>;
}

export function LifePlanLabRoute() {
  const { themeId } = useParams();
  if (!isFlowThemeId(themeId)) return <main className="p-8"><h1>Theme not found</h1><Link to="/life-plan-lab">Life Plan lab</Link></main>;
  return <LifePlanPlayer key={themeId} themeId={themeId} />;
}

export function LifePlanLabIndex() {
  const themes = FLOW_THEMES.filter(theme => theme.id === 'life_plan');
  return <main className="mx-auto max-w-5xl px-5 py-10" data-testid="life-plan-lab">
    <h1 className="text-3xl font-semibold">Life Plan lab</h1>
    <p className="mb-6 mt-2 text-sm">The real Life Plan (20 modules, 131 questions) with a fictional learner. Add <code>?module=8&amp;step=3&amp;answers=all</code> to a link.</p>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {themes.map(theme => <Link key={theme.id} to={`/life-plan-lab/${theme.id}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="h-24" style={{ background: theme.swatch }} aria-hidden="true" />
        <div className="p-4"><h2 className="font-semibold">{theme.label}</h2><p className="mt-1 text-sm">{theme.description}</p></div>
      </Link>)}
    </div>
  </main>;
}
